
import { Trans } from "@lingui/macro"
import SendOutlinedIcon from "@mui/icons-material/SendOutlined"
import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Stack,
    Typography,
} from "@mui/material"

import { PhotoList } from "components/asset/asset-details/asset-photo/photoList"
import { Asset, Document, PaginatedResponse, SessionResponse } from "models"

interface MatchAssetSummarySectionProps {
    asset: Asset
    sessionData: SessionResponse
    zoneLabel: string
    assetTitle: string
    priceLabel: string
    pricePerMqLabel: string
    highlightChips: string[]
    featureChips: string[]
    bathroomsValue: number | null
    photoData: PaginatedResponse<Document> | null
    userIsBrand: boolean
    canContactAccountManager: boolean
    onOpenContactModal: () => void
    formatNumber: (value?: number | null, fractionDigits?: number) => string
}

export const MatchAssetSummarySection = ({
    asset,
    sessionData,
    zoneLabel,
    assetTitle,
    priceLabel,
    pricePerMqLabel,
    highlightChips,
    featureChips,
    bathroomsValue,
    photoData,
    userIsBrand,
    canContactAccountManager,
    onOpenContactModal,
    formatNumber,
}: MatchAssetSummarySectionProps) => {
    return (
        <Box sx={{ border: "1px solid #d4d9e3", backgroundColor: "white" }}>
            <Box sx={{ backgroundColor: "#173e8d", color: "white", px: 3, py: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {zoneLabel}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {userIsBrand && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<SendOutlinedIcon />}
                                onClick={onOpenContactModal}
                                disabled={!canContactAccountManager}
                                sx={{ textTransform: "none", backgroundColor: "#00a3e5" }}
                            >
                                <Trans id="match-details.header.contact">Contattaci</Trans>
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Box>

            <Box sx={{ px: 3, py: 3 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    {highlightChips.map((label) => (
                        <Chip
                            key={label}
                            label={label}
                            size="small"
                            sx={{ backgroundColor: "#dff2ff", color: "#00a3e5", fontWeight: 600 }}
                        />
                    ))}
                </Stack>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        {photoData ? (
                            <PhotoList
                                asset={asset}
                                sessionData={sessionData}
                                photoData={photoData}
                                editableStatus={{
                                    value: asset.characteristics?.status ?? "",
                                    isEditing: false,
                                }}
                                onStatusDoubleClick={() => {}}
                                onStatusChange={(_status) => undefined}
                                onStatusKeyDown={(_event) => undefined}
                                onStatusBlur={() => {}}
                                statusOptions={[]}
                            />
                        ) : (
                            <Typography color="text.secondary" sx={{ p: 2 }}>
                                <Trans id="asset-details.gallery.empty">Nessuna immagine disponibile.</Trans>
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Stack spacing={2}>
                            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "flex-start" }} spacing={2}>
                                <Typography variant="h5" fontWeight={700} sx={{ color: "#273345" }}>
                                    {assetTitle}
                                </Typography>
                                <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: "#273345", lineHeight: 1 }}>
                                        {priceLabel}
                                    </Typography>
                                    <Typography color="text.secondary">{pricePerMqLabel}</Typography>
                                </Box>
                            </Stack>

                            <Divider />

                            <Grid container>
                                <Grid item xs={6} md={3} textAlign="center" sx={{ borderRight: { md: "1px solid #d4d9e3" } }}>
                                    <Typography variant="caption" color="text.secondary"><Trans id="match-details.kpi.gla">mq totali</Trans></Typography>
                                    <Typography variant="h6" fontWeight={700}>{formatNumber(asset.characteristics?.gla)}</Typography>
                                </Grid>
                                <Grid item xs={6} md={3} textAlign="center" sx={{ borderRight: { md: "1px solid #d4d9e3" } }}>
                                    <Typography variant="caption" color="text.secondary"><Trans id="match-details.kpi.height">mt altezza</Trans></Typography>
                                    <Typography variant="h6" fontWeight={700}>{formatNumber(asset.characteristics?.height, 2)}</Typography>
                                </Grid>
                                <Grid item xs={6} md={3} textAlign="center" sx={{ borderRight: { md: "1px solid #d4d9e3" }, mt: { xs: 2, md: 0 } }}>
                                    <Typography variant="caption" color="text.secondary"><Trans id="match-details.kpi.windows">Vetrine</Trans></Typography>
                                    <Typography variant="h6" fontWeight={700}>{formatNumber(asset.characteristics?.shopWindows)}</Typography>
                                </Grid>
                                <Grid item xs={6} md={3} textAlign="center" sx={{ mt: { xs: 2, md: 0 } }}>
                                    <Typography variant="caption" color="text.secondary"><Trans id="match-details.kpi.bathrooms">Bagni</Trans></Typography>
                                    <Typography variant="h6" fontWeight={700}>{formatNumber(bathroomsValue)}</Typography>
                                </Grid>
                            </Grid>

                            <Divider />

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {featureChips.map((label) => (
                                    <Chip
                                        key={label}
                                        label={label}
                                        size="small"
                                        sx={{ backgroundColor: "#d6f4ee", color: "#2a7f75", fontWeight: 600 }}
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}
